import _ from "lodash";
import React, { Component } from "react";
import PropTypes from "prop-types";

import { FormContext, FormControl } from "./FormContext";

class Form extends Component {
    constructor(props) {
        super(props);
        const formControl = new FormControl();
        this.state = {
            valid: formControl.valid,
            controlling: {},
            pFields: [],
            dirty: formControl.dirty,
            elements: formControl.elements,
            handleChange: this.handleChange.bind(this),
            name: this.props.name,
            addPField: this.props.trackPFields ? this.addPField.bind(this) : null,
            registerFormElement: this.registerFormElement.bind(this),
            deregisterFormElement: this.deregisterFormElement.bind(this),
            isFormElementRegistered: this.isFormElementRegistered.bind(this),
            updateFormElement: this.updateFormElement.bind(this),
            shouldUpdateForm: this.shouldUpdateForm.bind(this),
            getValues: this.getValues.bind(this),
            onSubmit: this.onSubmit.bind(this),
            onReset: this.onReset.bind(this),
            reset: this.reset.bind(this)
        };
        this.gavElements = ["city", "state", "zip", "streetAddress"];
        this.formRef = React.createRef();
    }

    componentDidMount() {
        this.state.valid = this.isValid(this.state.elements);
        this.props.onDidMount(this.state);
    }

    componentDidUpdate() {
        this.state.valid = this.isValid(this.state.elements);
        this.props.onDidUpdate(this.state);
    }

    getValues() {
        const values = {};
        _.map(this.state.elements, (element, key) => {
            values[key] = element.value;
        });
        return values;
    }

    reset(fieldNames = Object.keys(this.state.elements)) {
        const elements = _.clone(this.state.elements);
        Object.keys(elements).forEach(elemKey => {
            if (fieldNames.includes(elemKey)) {
                elements[elemKey].value = '';
            }
        });
        this.setState({
            elements
        });
    }

    isValid(elements = this.state.elements) {
        const elementNames = Object.keys(elements);
        if (elementNames.length) {
            return elementNames.every(key => elements[key].valid);
        }
        return false;
    }

    shouldUpdateForm(element) {
        let checks = ["valid", "value"];
        return checks.some(attr => this.state.elements[element.name][attr] !== element[attr]);
    }

    updateFormElement(element) {
        if (!this.shouldUpdateForm(element)) return;
        let newState = Object.assign({}, this.state);
        newState.elements[element.name].value = element.value;
        newState.elements[element.name].valid = element.valid;
        this.setState(newState);
    }

    registerFormElement(element) {
        this.state.elements[element.name] = {
            value: element.value,
            valid: element.valid,
            changed: false,
            validate: element.validate
        };

        if (element.specialParser) {
            this.state.elements[element.name].specialParser = element.specialParser;
        }
        if (element.handleChange) {
            this.state.elements[element.name].handleChange = element.handleChange;
        }
        if (element.dependentFields) {
            this.state.elements[element.name].dependentFields = element.dependentFields;
            _.forEach(element.dependentFields, (dependent) => {
                const currentControlling = this.state.controlling;
                if (currentControlling[dependent] === undefined) {
                    currentControlling[dependent] = [];
                }
                currentControlling[dependent].push(element.name);
                this.setState({ controlling: currentControlling });
            });
        }
    }

    deregisterFormElement(element) {
        if (this.state.elements[element.name]) {
            delete this.state.elements[element.name]
        }
    }

    isFormElementRegistered(name) {
        return !!this.state.elements[name];
    }

    handleChange(e, isValid) {
        let { dirty, valid, elements } = this.state;
        let { name, value } = e.target;
        const elem = elements[name];

        if (e.target.type === "checkbox") {
            let curValue = elem.value || [];

            if (!e.target.checked) {
                curValue = curValue.filter(i => i !== value);
            } else {
                curValue.push(value);
            }

            value = _.uniq(curValue);
            curValue.length = 0;
        }
        const hasChanged = elem && elem.value !== value;

        elem.value = value;
        elem.valid = isValid;
        elem.changed = e.target.type === "radio" ? true : hasChanged;

        if (elem.specialParser) {
            elem.special = elem.specialParser(e);
        }

        dirty = true;
        valid = this.isValid(elements);
        elements = _.forEach(elements, (element, key) => {
            if (key !== name) {
                element.changed = false;
            }
        });

        this.setState(
            {
                elements,
                dirty,
                valid
            }
        );
        this.props.onChange(name, elements[name], this.state);
    }

    onSubmit(e) {
        _.forEach(this.state.elements, (elem, key) => {
            const { value } = elem;
            const event = { target: { name: key, value } };
            let valid = true;

            if (elem.validate) {
                if (elem.special) {
                    valid = !elem.validate(value, {
                        target: { gaResponse: elem.special, name: key }
                    });
                } else {
                    valid = !elem.validate(value, event);
                }
            }

            elem.valid = valid;
        });

        if (this.isValid()) {
            this.props.onSubmit(e, this.state);
        } else {
            this.scrollToError();
        }
    }

    onReset(e) {
        this.props.onReset(e, this.state);
    }

    getAbsoluteOffsetTop(element) {
        let offsetTop = 0;
        while(element) {
            offsetTop += element.offsetTop;
            element = element.offsetParent;
        }
        return offsetTop;
    }

    scrollToError() {
        let scrollToElemName = _.chain(this.state.elements)
            .toPairs()
            .find(pair => !pair[1].valid)
            .first()
            .value();

        let erroredElem = Array.from(this.formRef.current).find(
            elem => elem && elem.getAttribute("name") === scrollToElemName
        );

        if (erroredElem) {
            window.scrollTo(0, this.getAbsoluteOffsetTop(erroredElem) - (window.innerHeight / 4));
        } else {
            scrollToElemName =
                this.gavElements.indexOf(scrollToElemName) !== -1
                    ? "googleAddressVerifyInput"
                    : scrollToElemName;

            erroredElem = Array.from(this.formRef.current).find(
                elem => elem && elem.getAttribute("name") === scrollToElemName
            );

            if (erroredElem) {
                window.scrollTo(0, this.getAbsoluteOffsetTop(erroredElem) - (window.innerHeight / 4));
            }
        }
    }

    addPField(fieldName) {
        const currentFields = this.state.pFields && Array.isArray(this.state.pFields) ? this.state.pFields.slice() : [];
        if(!_.includes(currentFields, fieldName)) {
            currentFields.push(fieldName);
        }
        this.setState({
            pFields: currentFields
        });
    }

    render() {
        const { debug, id, name, children } = this.props;

        return (
            <FormContext.Provider value={this.state}>
                <div>
                    {debug && (
                        <div id="form-debug-panel">
                            <p>dirty: {this.state.dirty.toString()}</p>
                            <p>valid: {this.state.valid.toString()}</p>
                        </div>
                    )}
                    <form id={id}
                          name={name}
                          onSubmit={this.onSubmit}
                          onReset={this.state.onReset}
                          ref={this.formRef} noValidate>
                        {children}
                    </form>
                </div>
            </FormContext.Provider>
        );
    }
}

Form.defaultProps = {
    debug: false,
    trackPFields: false,
    onChange: () => { },
    onDidMount: () => { },
    onDidUpdate: () => { },
    onSubmit: () => { },
    onReset: () => { }
};

Form.propTypes = {
    debug: PropTypes.bool,
    trackPFields: PropTypes.bool,
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func,
    onDidMount: PropTypes.func,
    onDidUpdate: PropTypes.func,
    onSubmit: PropTypes.func,
    onReset: PropTypes.func
};

export default Form;
